/* ============================================================================
   ahcut glyphs — the desktop app's own line icons, shared by web + dashboard
   ----------------------------------------------------------------------------
   The app draws every icon itself (desktop repo src/components/ui/glyphPaths.tsx:
   a 24px box, 1.5 stroke, round caps and joins, currentColor) and so does this
   site — no icon library (ui/DESIGN.md §2). The first group is a copy of the
   app's paths at v2.36.0, under the app's names; `download` is the app's
   `import` glyph, `theme` its theme glyph (one icon for the toggle) and `chip`
   the glyph of its "AI & MCP" settings page (the dashboard's "Koneksi AI"). The
   second group exists only here, drawn to the same rules. Rendered by
   web/src/components/ui/Glyph.astro and dashboard/src/components/ui/glyph.tsx.
   ============================================================================ */

export type GlyphShape = readonly [tag: "path" | "circle" | "rect", attrs: Readonly<Record<string, string>>];

export const GLYPHS = {
  // --- the app's glyphs (glyphPaths.tsx, v2.36.0) ---
  check: [["path", { d: "M5 12.5l4 4 10-10" }]],
  close: [["path", { d: "M6.5 6.5l11 11M17.5 6.5l-11 11" }]],
  chevronDown: [["path", { d: "M7 10l5 5 5-5" }]],
  chevronLeft: [["path", { d: "M14 6l-6 6 6 6" }]],
  chevronRight: [["path", { d: "M10 7l5 5-5 5" }]],
  copy: [
    ["rect", { x: "8.5", y: "3.5", width: "11", height: "14", rx: "2" }],
    ["path", { d: "M11.5 8h5M11.5 11.5h5M5.5 7v11.5a2 2 0 0 0 2 2h9" }],
  ],
  external: [
    ["path", { d: "M14 4h6v6M20 4l-9 9" }],
    ["path", { d: "M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" }],
  ],
  info: [
    ["circle", { cx: "12", cy: "12", r: "8.5" }],
    ["path", { d: "M12 11v5M12 7.6v.1" }],
  ],
  key: [
    ["circle", { cx: "8", cy: "15", r: "4" }],
    ["path", { d: "M11 12l9-9M16 7l3 3" }],
  ],
  bolt: [["path", { d: "M13.5 3L6 13.5h5.5L10.5 21 18 10.5h-5.5z" }]],
  plus: [["path", { d: "M12 5v14M5 12h14" }]],
  success: [
    ["circle", { cx: "12", cy: "12", r: "8.5" }],
    ["path", { d: "M8 12.3l2.7 2.7L16.2 9.5" }],
  ],
  warning: [
    ["path", { d: "M12 4l9 16H3z" }],
    ["path", { d: "M12 10v4.5M12 17.3v.1" }],
  ],
  error: [
    ["circle", { cx: "12", cy: "12", r: "8.5" }],
    ["path", { d: "M12 7.5v5.5M12 16.3v.1" }],
  ],
  download: [["path", { d: "M12 4v10.5M8 10.5l4 4 4-4M5 13.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4.5" }]],
  unlink: [
    ["path", { d: "M13 6.5l1-1a4 4 0 0 1 5.7 5.7l-1 1" }],
    ["path", { d: "M11 17.5l-1 1a4 4 0 0 1-5.7-5.7l1-1" }],
    ["path", { d: "M8 3.5V6M3.5 8H6M16 20.5V18M20.5 16H18" }],
  ],
  theme: [
    ["circle", { cx: "12", cy: "12", r: "8" }],
    ["path", { d: "M12 4v16M12 8h5M12 12h7M12 16h5" }],
  ],
  person: [
    ["circle", { cx: "12", cy: "8.5", r: "3.5" }],
    ["path", { d: "M5 20c1.4-3.4 4-5 7-5s5.6 1.6 7 5" }],
  ],
  layout: [
    ["rect", { x: "3.5", y: "4.5", width: "17", height: "15", rx: "2" }],
    ["path", { d: "M9 4.5v15M9 14h11.5" }],
  ],
  link: [
    ["path", { d: "M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" }],
    ["path", { d: "M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" }],
  ],
  help: [
    ["circle", { cx: "12", cy: "12", r: "8.5" }],
    ["path", { d: "M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.3M12 16.8v.1" }],
  ],
  logs: [
    ["path", { d: "M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" }],
    ["path", { d: "M7 12h10M7 15h6" }],
  ],
  chip: [
    ["rect", { x: "6", y: "6", width: "12", height: "12", rx: "2" }],
    ["path", { d: "M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" }],
  ],

  // --- drawn for the site, to the app's rules ---
  arrowLeft: [["path", { d: "M19 12H5M11 6l-6 6 6 6" }]],
  arrowRight: [["path", { d: "M5 12h14M13 6l6 6-6 6" }]],
  menu: [["path", { d: "M4 7h16M4 12h16M4 17h16" }]],
  book: [
    ["path", { d: "M12 6.5C10 5 7 4.5 4 5v13.5c3-.5 6 0 8 1.5 2-1.5 5-2 8-1.5V5c-3-.5-6 0-8 1.5z" }],
    ["path", { d: "M12 6.5V20M7 9h2.5M7 12h2.5M14.5 9H17M14.5 12H17" }],
  ],
  rss: [
    ["circle", { cx: "6", cy: "18", r: "1.3" }],
    ["path", { d: "M5 11a8 8 0 0 1 8 8M5 5a14 14 0 0 1 14 14" }],
  ],
  cornerDownRight: [["path", { d: "M5 4v7a3 3 0 0 0 3 3h11M15 10l4 4-4 4" }]],
  logIn: [["path", { d: "M14 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M4 12h10M10 8l4 4-4 4" }]],
  logOut: [["path", { d: "M10 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3M20 12H10M16 8l4 4-4 4" }]],
  printer: [
    ["path", { d: "M7 9V4.5h10V9" }],
    ["rect", { x: "4", y: "9", width: "16", height: "7", rx: "2" }],
    ["path", { d: "M7 13.5h10V20H7z" }],
  ],
  receipt: [
    ["path", { d: "M6 3.5h12v17l-2.4-1.5-2.4 1.5-2.4-1.5-2.4 1.5L6 20.5z" }],
    ["path", { d: "M9 8h6M9 11.5h6M9 15h3" }],
  ],
  cart: [
    ["path", { d: "M3.5 4.5h2l2.2 10a1.5 1.5 0 0 0 1.5 1.2h7.6a1.5 1.5 0 0 0 1.4-1.1l1.6-6.1H6.4" }],
    ["circle", { cx: "9.5", cy: "19.5", r: "1.3" }],
    ["circle", { cx: "16.5", cy: "19.5", r: "1.3" }],
  ],
  spinner: [["path", { d: "M12 3.5a8.5 8.5 0 1 0 8.5 8.5" }]],
  users: [
    ["circle", { cx: "9", cy: "8.5", r: "3.2" }],
    ["path", { d: "M3.5 19c1-3 3.2-4.6 5.5-4.6s4.5 1.6 5.5 4.6" }],
    ["path", { d: "M15.5 5.6a3 3 0 0 1 0 5.8M17.2 14.6c1.6.6 2.7 2 3.3 4.4" }],
  ],
  package: [
    ["path", { d: "M12 3.5l7.5 4v9L12 20.5l-7.5-4v-9z" }],
    ["path", { d: "M4.5 7.5L12 11.5l7.5-4M12 11.5v9" }],
  ],
  tag: [
    ["path", { d: "M3.5 11.5v-7a1 1 0 0 1 1-1h7l8.6 8.6a1.5 1.5 0 0 1 0 2.1l-6 6a1.5 1.5 0 0 1-2.1 0z" }],
    ["circle", { cx: "8", cy: "8", r: "1.3" }],
  ],
  ticket: [
    ["path", { d: "M3.5 7.5a1 1 0 0 1 1-1h15a1 1 0 0 1 1 1V10a2 2 0 0 0 0 4v2.5a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1V14a2 2 0 0 0 0-4z" }],
    ["path", { d: "M14.5 7v1.5M14.5 11.25v1.5M14.5 15.5V17" }],
  ],
  coins: [
    ["circle", { cx: "9.5", cy: "9.5", r: "5.5" }],
    ["path", { d: "M14.6 7.4a5.5 5.5 0 1 1-7.2 7.2" }],
  ],
} as const satisfies Record<string, readonly GlyphShape[]>;

export type GlyphName = keyof typeof GLYPHS;
